import React, { useEffect, useRef } from 'react';
import Icon from './OpenPlayIcon';

const ContextualActionSheet = ({
  isOpen,
  onClose,
  title,
  children,
  height = 'auto',
  showCloseButton = true,
}) => {
  const sheetRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;

    if (diff > 0 && sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${diff}px)`;
    }
  };

  const handleTouchEnd = () => {
    const diff = currentY.current - startY.current;

    if (diff > 100) {
      onClose();
    }

    if (sheetRef.current) {
      sheetRef.current.style.transform = '';
    }

    startY.current = 0;
    currentY.current = 0;
  };

  const getHeightClass = () => {
    switch (height) {
      case 'full':
        return 'h-[calc(100vh-80px)] md:h-auto md:max-h-[90vh]';
      case 'half':
        return 'h-[50vh] md:h-auto md:max-h-[60vh]';
      default:
        return 'h-auto max-h-[85vh] md:max-h-[80vh]';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal">
      <div className="absolute inset-0 bg-secondary" onClick={onClose} />

      <div
        ref={sheetRef}
        className={`
          absolute bottom-0 left-0 right-0
          bg-card rounded-t-xl shadow-elevation-5
          transition-smooth md:hidden
          ${getHeightClass()}
        `}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
      >
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 rounded-full bg-muted" />
        </div>

        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            {title && <h3 className="text-lg font-semibold font-heading text-foreground">{title}</h3>}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth touch-target"
                aria-label="닫기"
              >
                <Icon name="XMarkIcon" size={24} />
              </button>
            )}
          </div>
        )}

        <div className="overflow-y-auto px-6 py-6">{children}</div>
      </div>

      <div className="hidden md:flex items-center justify-center min-h-screen p-6">
        <div
          className={`
            relative w-full max-w-2xl bg-card rounded-xl shadow-elevation-5
            ${getHeightClass()}
          `}
        >
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between px-8 py-6 border-b border-border">
              {title && <h3 className="text-xl font-semibold font-heading text-foreground">{title}</h3>}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className="flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth touch-target"
                  aria-label="닫기"
                >
                  <Icon name="XMarkIcon" size={24} />
                </button>
              )}
            </div>
          )}

          <div className="overflow-y-auto px-8 py-6">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default ContextualActionSheet;
